import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ViewGate } from './components/ViewGate';
import { AuthProvider } from './contexts/AuthContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { AudioStreamsProvider } from './contexts/AudioStreamsContext';
import { TickerLinkProvider } from './contexts/TickerLinkContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { AudioDrawer } from './components/AudioDrawer';
import { Analytics } from './components/Analytics';
import { UnclaimedCourseBanner } from './components/UnclaimedCourseToast';
import { SetupPromptBanner } from './components/SetupPromptToast';
import { ReloadBanner } from './components/ReloadBanner';
import { VersionUpdateToast } from './components/VersionUpdateToast';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { CoursesPage } from './pages/CoursesPage';
import { StripeWelcomePage } from './pages/StripeWelcomePage';
import { GexRayPopoutPage } from './pages/GexRayPopoutPage';
import { CandleChartPopoutPage } from './pages/CandleChartPopoutPage';
import { ZerodayBreadthPopoutPage } from './pages/ZerodayBreadthPopoutPage';
import { TickerDetailPage } from './pages/TickerDetailPage';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import { MarketOverviewPage } from './pages/MarketOverviewPage';
import { CalendarPage } from './pages/CalendarPage';
import { AppShell } from './components/shell/AppShell';
import { MobileWorkspace } from './mobile/MobileWorkspace';
import { useShellMode } from './mobile/useShellMode';
import { legacyMobilePathToShared } from './mobile/legacyMobileRedirects';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

/**
 * Authenticated chrome: mobile shell vs desktop AppShell.
 * Same routes; no /m path tree (QF WorkspaceShell pattern).
 */
function AuthenticatedShell(): React.ReactElement {
  const mode = useShellMode();
  if (mode === 'mobile') {
    return <MobileWorkspace />;
  }
  return <AppShell />;
}

/** One-way redirect from legacy /m/* bookmarks to shared routes. */
function LegacyMobileRedirect(): React.ReactElement {
  const location = useLocation();
  const target = legacyMobilePathToShared(location.pathname) ?? '/';
  return <Navigate to={target + location.search} replace />;
}

/** Desktop-only audio drawer — mobile uses Audio tab (PR8). */
function DesktopAudioDrawer(): React.ReactElement | null {
  const mode = useShellMode();
  if (mode === 'mobile') return null;
  return <AudioDrawer />;
}

// Temporary redirects for old email links (remove after 30 days)
function StripeClaimRedirect(): React.ReactElement {
  const { token } = useParams();
  return <Navigate to={`/stripe/welcome?token=${token}`} replace />;
}

/** Placeholder route so /audio is a real path under the shell. */
function AudioRoutePage(): React.ReactElement {
  // Mobile shell mounts MobileAudioTab from the registry when path is /audio.
  // Desktop: send users home — audio is the drawer.
  return <Navigate to="/" replace />;
}

function AppRoutes(): React.ReactElement {
  return (
    <>
      <ImpersonationBanner />
      <DesktopAudioDrawer />
      <UnclaimedCourseBanner />
      <SetupPromptBanner />
      <ReloadBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Legacy /m/* → shared routes (bookmarks only; dual tree removed) */}
        <Route path="/m/*" element={<LegacyMobileRedirect />} />
        <Route path="/m" element={<LegacyMobileRedirect />} />

        {/* Authenticated routes — shell swaps chrome, not path */}
        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedShell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route
            path="/explorer"
            element={
              <ViewGate viewType="ticker-explorer">
                <TickerDetailPage />
              </ViewGate>
            }
          />
          <Route
            path="/explorer/:symbol"
            element={
              <ViewGate viewType="ticker-explorer">
                <TickerDetailPage />
              </ViewGate>
            }
          />
          <Route
            path="/market"
            element={
              <ViewGate viewType="market-overview">
                <MarketOverviewPage />
              </ViewGate>
            }
          />
          <Route
            path="/calendar"
            element={
              <ViewGate viewType="calendars">
                <CalendarPage />
              </ViewGate>
            }
          />
          <Route path="/audio" element={<AudioRoutePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/courses" element={<CoursesPage />} />
        </Route>

        <Route path="/stripe/claim/:token" element={<StripeClaimRedirect />} />
        <Route path="/courses/claim/:token" element={<Navigate to="/courses" replace />} />
        <Route path="/stripe/welcome" element={<StripeWelcomePage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DashboardProvider>
          <WebSocketProvider>
            <AudioStreamsProvider>
              <TickerLinkProvider>
                <BrowserRouter>
                  <Analytics />
                  <VersionUpdateToast />
                  <Routes>
                    <Route
                      path="/gexray-monitor"
                      element={
                        <ProtectedRoute>
                          <GexRayPopoutPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/popout/candle-chart/:windowId"
                      element={
                        <ProtectedRoute>
                          <CandleChartPopoutPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/popout/zeroday-breadth/:windowId"
                      element={
                        <ProtectedRoute>
                          <ZerodayBreadthPopoutPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ticker/:symbol"
                      element={
                        <ProtectedRoute>
                          <TickerDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/diagnostics" element={<DiagnosticsPage />} />
                    <Route path="*" element={<AppRoutes />} />
                  </Routes>
                </BrowserRouter>
              </TickerLinkProvider>
            </AudioStreamsProvider>
          </WebSocketProvider>
        </DashboardProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
